import React from 'react'
import Checkbox from '@material-ui/core/Checkbox'
import CircularProgress from '@material-ui/core/CircularProgress'
import { Set } from 'immutable'
// src imports
import { CardPanelView } from 'src/orders/components'
import { CatalogRibbon } from 'src/catalog/components'
import { Button, Confirm, Transition } from 'src/chrome'
import { useActions, useMutations, useQueries, useState } from 'src/hooks'
import { getCard, getDraftedCards } from 'src/graphql'
// style imports
import chromeStyles from 'src/chrome/styles/Chrome.module.scss'
import styles from './drafts.module.scss'
import suspenseBoundary from '../../chrome/SuspenseBoundary/suspenseBoundaryHOC'

type Props = {
  isWrapContentEnabled?: boolean
}

const DraftedCards: React.FC<Props> = props => {
  const { isWrapContentEnabled } = props

  const actions = useActions()
  const mutations = useMutations()

  const [
    isDeleteConfirmationModalOpen,
    setIsDeleteConfirmationModalOpen,
  ] = useState(false)

  const [selectedDraftedCardIds, setSelectedDraftedCardIds] = useState<
    Set<string>
  >(Set())

  const [previewedDraftedCardId, setPreviewedDraftedCardId] = useState<
    string | undefined
  >(undefined)

  const [
    {
      results: draftedCards,
      isLoadingMore: isLoadingMoreDraftedCards,
      hasMore: hasMoreDraftedCards,
      loadMore: loadMoreDraftedCards,
    },
    previewedDraftedCard,
  ] = useQueries(
    getDraftedCards(),
    previewedDraftedCardId
      ? getCard({ id: previewedDraftedCardId })
      : undefined,
  )

  const isEveryDraftedCardSelected =
    selectedDraftedCardIds.size === draftedCards.length

  const previewDraftedCardWithId = (draftId: string) => {
    setPreviewedDraftedCardId(draftId)
  }

  const clearPreviewedDraftedCardId = () => {
    setPreviewedDraftedCardId(undefined)
  }

  const openPreviewedDraftedCard = () => {
    if (previewedDraftedCardId) {
      actions.openCard(previewedDraftedCardId)
      clearPreviewedDraftedCardId()
    }
  }

  const deleteSelectedDraftedCards = () => {
    mutations.deleteCards({ cards: Array.from(selectedDraftedCardIds) })
    setSelectedDraftedCardIds(Set())
    toggleDeleteConfirmationModal()
  }

  const handleSelectDraftedCardWithId = (cardId: string) => {
    const set = selectedDraftedCardIds.has(cardId)
      ? selectedDraftedCardIds.delete(cardId)
      : selectedDraftedCardIds.add(cardId)
    setSelectedDraftedCardIds(set)
  }

  const handleSelectAllDraftedCards = () => {
    setSelectedDraftedCardIds(
      Set(isEveryDraftedCardSelected ? [] : draftedCards.map(card => card.id)),
    )
  }

  const toggleDeleteConfirmationModal = () => {
    setIsDeleteConfirmationModalOpen(x => !x)
  }

  return (
    <div>
      {isLoadingMoreDraftedCards && (
        <div className={chromeStyles.centeredLoader}>
          <CircularProgress />
        </div>
      )}

      <div className={styles.body}>
        {selectedDraftedCardIds.size > 0 && (
          <div className={styles.selectedMenu}>
            <div style={{ display: 'flex' }}>
              <Checkbox
                checked={isEveryDraftedCardSelected}
                onChange={handleSelectAllDraftedCards}
                style={{ width: 35 }}
              />
              <span>{`Selected ${selectedDraftedCardIds.size} of ${draftedCards.length}`}</span>
            </div>
            <Button
              title={'Delete'}
              buttonColor={'pink'}
              onClick={toggleDeleteConfirmationModal}
            />
          </div>
        )}
        <div style={{ marginTop: '15%' }}>
          <CatalogRibbon
            isWrapContentEnabled={isWrapContentEnabled}
            items={draftedCards}
            onClick={previewDraftedCardWithId}
            type={'drafts'}
            loadMore={hasMoreDraftedCards ? loadMoreDraftedCards : undefined}
            isLoadingMore={isLoadingMoreDraftedCards}
            hasMore={hasMoreDraftedCards}
            selectedSet={selectedDraftedCardIds}
            handleCheckbox={handleSelectDraftedCardWithId}
          />
        </div>
      </div>
      {previewedDraftedCardId && (
        <Confirm
          title={'Would you like to finish your card?'}
          confirmTitle={'Finish Card'}
          onConfirm={openPreviewedDraftedCard}
          onDecline={clearPreviewedDraftedCardId}
          childType={'image'}
          containerClassName={styles.confirmContainer}
          wrapperClassName={styles.confirmWrapper}
          isModalEnabled={true}
          confirmMessageClassName={styles.confirmMessage}
        >
          <div style={{ minHeight: 200, minWidth: '100%' }}>
            {previewedDraftedCard && (
              <CardPanelView
                card={previewedDraftedCard}
                sliderMaxContainerHeight={750}
              />
            )}
          </div>
        </Confirm>
      )}
      {isDeleteConfirmationModalOpen && (
        <Confirm
          title={`Delete ${selectedDraftedCardIds.size} cards`}
          message={'Are you sure you want to delete the selected cards?'}
          confirmTitle={'Delete'}
          onConfirm={deleteSelectedDraftedCards}
          onDecline={toggleDeleteConfirmationModal}
        />
      )}
    </div>
  )
}

export default suspenseBoundary({
  component: DraftedCards,
  unresolved: <Transition message={'Loading...'} />,
  failure: error => `Failed loading contacts: ${error}`,
})
